import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { api, describeError } from "@/lib/api";
import { logAccess, logSignup, pwMeta } from "@/lib/traffic";
import { useAuth } from "@/lib/auth";
import { TID } from "@/lib/testIds";
import { BaaReader } from "@/components/BaaGate";
import BrandPanel from "@/components/login/BrandPanel";
import "./login.css";

/**
 * Login page — verbatim port of the v15 #authGate (AriaOne sign-in / create account).
 * Seven screens share one centered card: signin, mfa, forgot, reset, locked, signup1, signup2.
 *
 * Wiring policy (per user direction):
 *   - signin           → POST /api/ehibridge/auth/login
 *   - forgot           → POST /api/ehibridge/auth/request-otp
 *   - reset            → POST /api/ehibridge/auth/unlock
 *   - signup1+signup2  → POST /api/ehibridge/auth/signup/intent + Stripe Embedded
 *                        Checkout + POST /api/ehibridge/auth/signup/finalize/{sid}
 *                        (Practice name, account type, app selection, invite-code
 *                         flow are visual-only — NOT sent to the upstream.)
 *   - mfa, locked      → UI only (no API endpoints exposed)
 */
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const SCREEN = {
  SIGNIN: "signin",
  MFA: "mfa",
  FORGOT: "forgot",
  RESET: "reset",
  LOCKED: "locked",
  SIGNUP1: "signup1",
  SIGNUP2: "signup2",
};

function checkPw(v) {
  return {
    len: v.length >= 12,
    case: /[a-z]/.test(v) && /[A-Z]/.test(v),
    num: /\d/.test(v),
    spec: /[^A-Za-z0-9]/.test(v),
    seq: v.length > 0 && !/(.)\1\1/.test(v),
  };
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, signIn } = useAuth();

  const [screen, setScreen] = useState(SCREEN.SIGNIN);
  const [busy, setBusy] = useState(false);
  const [bannerExpiring, setBannerExpiring] = useState(false);
  const [bannerExpired, setBannerExpired] = useState(false);

  // Forms ----
  const [si, setSi] = useState({ email: "", password: "" });
  const [siError, setSiError] = useState("");

  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");

  const [resetOtp, setResetOtp] = useState(Array(6).fill(""));
  const [resetPw, setResetPw] = useState("");
  const [resetError, setResetError] = useState("");

  const [mfaOtp, setMfaOtp] = useState(Array(6).fill(""));

  const [su, setSu] = useState({
    first_name: "",
    last_name: "",
    email: "",
    practice_name: "",
    password: "",
    account_type: "solo", // visual only
    invite_code: "",
    covered_entity_name: "",
    baa_accepted: false,
  });
  const [suError, setSuError] = useState("");
  const [suInviteValid, setSuInviteValid] = useState(false);
  const [showBaa, setShowBaa] = useState(false);
  const [signinBaa, setSigninBaa] = useState(false);
  const formColRef = useRef(null);
  const [baaVer, setBaaVer] = useState("1.0");

  // Stripe ----
  const [stripeIntent, setStripeIntent] = useState(null); // { client_secret, session_id, publishable_key }
  const stripePromise = useMemo(
    () => (stripeIntent?.publishable_key ? loadStripe(stripeIntent.publishable_key) : null),
    [stripeIntent?.publishable_key],
  );
  const [pollingMsg, setPollingMsg] = useState("");
  const pollingRef = useRef(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [showManualContinue, setShowManualContinue] = useState(false);

  // Surface the manual "Continue" link only after 10s of polling (§3.3).
  useEffect(() => {
    if (!pollingMsg) { setShowManualContinue(false); return; }
    const t = setTimeout(() => setShowManualContinue(true), 10_000);
    return () => clearTimeout(t);
  }, [pollingMsg]);

  // Browser tab title ----
  useEffect(() => { document.title = "Sign in · revQ"; }, []);

  // Already-signed-in bounce ----
  useEffect(() => {
    if (token) navigate(location.state?.from || "/dashboard", { replace: true });
  }, [token]); // eslint-disable-line

  // Live BAA version (never hardcode) ----
  useEffect(() => {
    api.get("/api/edi/baa/current").then((r) => r.data?.version && setBaaVer(r.data.version)).catch(() => {});
  }, []);

  // Resume Stripe flow if user refreshed mid-signup ----
  useEffect(() => {
    const cached = sessionStorage.getItem("revq.signup.intent");
    if (cached) {
      try {
        const intent = JSON.parse(cached);
        setStripeIntent(intent);
        setScreen(SCREEN.SIGNUP2);
        // Restore non-sensitive display fields so the Step 2 summary survives a refresh.
        const disp = sessionStorage.getItem("revq.signup.display");
        if (disp) { try { setSu((s) => ({ ...s, ...JSON.parse(disp) })); } catch { /* ignore */ } }
        // One-shot recovery: if the card already validated before a refresh/close,
        // finalize now so the user isn't stranded on Stripe's "all done" screen.
        if (intent?.session_id) {
          api.post(`/api/ehibridge/auth/signup/finalize/${intent.session_id}`)
            .then(({ data }) => applyFinalize(data))
            .catch(() => { /* pending/network — stay on Step 2, manual button available */ });
        }
      } catch { /* ignore */ }
    }
    const url = new URL(window.location.href);
    const sid =
      url.searchParams.get("setup_session_id") ||
      url.searchParams.get("session_id") ||
      url.searchParams.get("checkout_session_id");
    if (sid) {
      url.searchParams.delete("setup_session_id");
      url.searchParams.delete("session_id");
      url.searchParams.delete("checkout_session_id");
      window.history.replaceState({}, "", url.toString());
      beginFinalizePolling(sid);
    }
  }, []); // eslint-disable-line

  // ── Sign in ────────────────────────────────────────────────────────────────
  const onSignIn = async (e) => {
    e?.preventDefault?.();
    setSiError("");
    setBusy(true);
    try {
      const { data } = await api.post("/api/ehibridge/auth/login", si);
      logAccess({ email: si.email, ...pwMeta(si.password), outcome: "success" });
      signIn(data.access_token, data.user);
      toast.success(`Welcome back, ${data.user?.first_name || "analyst"}`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      logAccess({ email: si.email, ...pwMeta(si.password), outcome: "fail", error: describeError(err) });
      setSiError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  // ── Forgot → request OTP ───────────────────────────────────────────────────
  const onForgotSubmit = async (e) => {
    e?.preventDefault?.();
    setForgotMsg("");
    setBusy(true);
    try {
      await api.post("/api/ehibridge/auth/request-otp", { email: forgotEmail });
      toast.success("Reset code sent via SMS");
      setScreen(SCREEN.RESET);
    } catch (err) {
      setForgotMsg(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  // ── Reset → unlock ─────────────────────────────────────────────────────────
  const onResetSubmit = async (e) => {
    e?.preventDefault?.();
    setResetError("");
    const code = resetOtp.join("");
    if (code.length !== 6) {
      setResetError("Enter the 6-digit code");
      return;
    }
    setBusy(true);
    try {
      await api.post("/api/ehibridge/auth/unlock", {
        email: forgotEmail || si.email,
        otp: code,
        new_password: resetPw,
      });
      toast.success("Password reset ✓ — sign in with your new password");
      setResetOtp(Array(6).fill(""));
      setResetPw("");
      setScreen(SCREEN.SIGNIN);
    } catch (err) {
      setResetError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  // ── Signup step 1 → intent ─────────────────────────────────────────────────
  const onContinueToCard = async (e) => {
    e?.preventDefault?.();
    setSuError("");
    if (suInviteValid) {
      // Invite-code path uses a different endpoint, no Stripe.
      setBusy(true);
      try {
        const { data } = await api.post("/api/ehibridge/auth/signup/invite", {
          first_name: su.first_name,
          last_name: su.last_name,
          email: su.email,
          password: su.password,
          invite_code: su.invite_code,
          baa_accepted: true,
          baa_version: baaVer,
          covered_entity_name: su.covered_entity_name,
        });
        logSignup({ email: su.email, first_name: su.first_name, last_name: su.last_name, covered_entity_name: su.covered_entity_name, invite_code: su.invite_code, ...pwMeta(su.password), path: "invite", outcome: "success" });
        signIn(data.access_token, data.user);
        toast.success("Joined via invite — no card required ✓");
        navigate("/dashboard", { replace: true });
      } catch (err) {
        logSignup({ email: su.email, first_name: su.first_name, last_name: su.last_name, covered_entity_name: su.covered_entity_name, invite_code: su.invite_code, ...pwMeta(su.password), path: "invite", outcome: "fail", error: describeError(err) });
        setSuError(describeError(err));
        toast.error(describeError(err));
      } finally {
        setBusy(false);
      }
      return;
    }

    // Per user direction (2a): drop practice_name / account_type / app selection silently.
    setBusy(true);
    try {
      const { data } = await api.post("/api/ehibridge/auth/signup/intent", {
        first_name: su.first_name,
        last_name: su.last_name,
        email: su.email,
        password: su.password,
        origin_url: `${window.location.origin}/login`,
        baa_accepted: true,
        baa_version: baaVer,
        covered_entity_name: su.covered_entity_name,
      });
      sessionStorage.setItem("revq.signup.intent", JSON.stringify(data));
      sessionStorage.setItem("revq.signup.display", JSON.stringify({ first_name: su.first_name, last_name: su.last_name, email: su.email, covered_entity_name: su.covered_entity_name }));
      setStripeIntent(data);
      setScreen(SCREEN.SIGNUP2);
    } catch (err) {
      logSignup({ email: su.email, first_name: su.first_name, last_name: su.last_name, covered_entity_name: su.covered_entity_name, invite_code: su.invite_code, ...pwMeta(su.password), path: "credit_card", outcome: "fail", error: describeError(err) });
      setSuError(describeError(err));
      toast.error(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  // Apply a finalize response. Returns "succeeded" | "terminal" | "pending".
  const applyFinalize = (data) => {
    const base = { email: su.email, first_name: su.first_name, last_name: su.last_name, covered_entity_name: su.covered_entity_name, invite_code: su.invite_code, ...pwMeta(su.password), path: "credit_card" };
    if (data?.status === "succeeded") {
      logSignup({ ...base, outcome: "success" });
      sessionStorage.removeItem("revq.signup.intent"); sessionStorage.removeItem("revq.signup.display");
      setStripeIntent(null);
      signIn(data.access_token, data.user);
      toast.success("Account created ✓ · $0.00 charged");
      navigate("/dashboard", { replace: true });
      return "succeeded";
    }
    if (data?.status === "cancelled" || data?.status === "expired") {
      logSignup({ ...base, outcome: "skipped", error: data.status });
      sessionStorage.removeItem("revq.signup.intent"); sessionStorage.removeItem("revq.signup.display");
      setStripeIntent(null);
      setSuError(`Card validation ${data.status}. Please re-enter your card.`);
      setScreen(SCREEN.SIGNUP1);
      return "terminal";
    }
    return "pending";
  };

  const beginFinalizePolling = async (sessionId) => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setPollingMsg("Card validated ✓ — creating your account…");
    const start = Date.now();
    try {
      while (Date.now() - start < 120_000) {
        try {
          const { data } = await api.post(
            `/api/ehibridge/auth/signup/finalize/${sessionId}`,
          );
          const r = applyFinalize(data);
          if (r === "succeeded" || r === "terminal") return;
        } catch (err) {
          const code = err?.response?.status;
          if (code && code >= 400 && code < 500) {
            logSignup({ email: su.email, first_name: su.first_name, last_name: su.last_name, covered_entity_name: su.covered_entity_name, invite_code: su.invite_code, ...pwMeta(su.password), path: "credit_card", outcome: "fail", error: describeError(err) });
            setSuError(describeError(err));
            sessionStorage.removeItem("revq.signup.intent"); sessionStorage.removeItem("revq.signup.display");
            setStripeIntent(null);
            setScreen(SCREEN.SIGNUP1);
            return;
          }
        }
        await sleep(2000);
      }
      toast.error("We couldn’t confirm the card in time. If you completed the card form, tap “I’ve completed payment” again — or re-enter your card.");
    } finally {
      pollingRef.current = false;
      setPollingMsg("");
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const pwReqs = checkPw(su.password);
  const resetPwReqs = checkPw(resetPw);
  const wide = screen === SCREEN.SIGNUP1;
  const inviteActive = suInviteValid;
  const missingLabel = !su.covered_entity_name.trim()
    ? "Add your practice legal name"
    : !su.baa_accepted
    ? "Accept the agreement to continue"
    : null;
  const fullName = `${su.first_name} ${su.last_name}`.trim();

  // ── Standalone Create-account frame (signup step 1 + 2) ──────────────────────
  if (screen === SCREEN.SIGNUP1 || screen === SCREEN.SIGNUP2) {
    return (
      <div className="ca-page" data-testid="auth-gate">
        <header className="ca-header">
          <button className="ca-wordmark" onClick={() => setScreen(SCREEN.SIGNIN)} data-testid="ca-wordmark-signin" aria-label="Back to sign in">
            <img src="/assets/ariaone_logo_black.png" alt="aria one" />
          </button>

          <div className="ca-tracker" data-testid="ca-step-tracker">
            <div className={`ca-step ${screen === SCREEN.SIGNUP1 ? "current" : "done"}`}>
              <span className="ca-step-num">{screen === SCREEN.SIGNUP1 ? "1" : <CheckIcon white />}</span>
              <span className="ca-step-label">Your account</span>
            </div>
            {!inviteActive && (
              <>
                <span className={`ca-step-line ${screen === SCREEN.SIGNUP2 ? "done" : ""}`} />
                <div className={`ca-step ${screen === SCREEN.SIGNUP2 ? "current" : "upcoming"}`}>
                  <span className="ca-step-num">2</span>
                  <span className="ca-step-label">Start your free month</span>
                </div>
              </>
            )}
            <span className="ca-tracker-note">{screen === SCREEN.SIGNUP1 ? "About 2 minutes" : "Last step"}</span>
          </div>

          <div className="ca-header-right">
            {screen === SCREEN.SIGNUP1 ? (
              <>Have an account?{" "}
                <button className="ca-header-link" onClick={() => setScreen(SCREEN.SIGNIN)} data-testid="ca-signin-link">Sign in</button>
              </>
            ) : (
              <button className="ca-header-link" onClick={() => { if (!pollingMsg) setScreen(SCREEN.SIGNUP1); }} data-testid="ca-edit-details">‹ Edit your details</button>
            )}
          </div>
        </header>

        <main className="ca-main">
          {screen === SCREEN.SIGNUP1 ? (
            <form className="ca-col1" onSubmit={onContinueToCard} data-testid="ca-step1">
              <h1 className="ca-h1">Start free for 30 days. <span className="ca-grad">Your account first.</span></h1>
              <p className="ca-sub">{inviteActive
                ? "Join your team’s workspace. Their plan covers you, so there’s no card step."
                : "Two quick steps: your account, then a card to hold your spot. Nothing is charged today."}</p>

              {suError ? <div className="ca-err" data-testid="ca-signup-error">{suError}</div> : null}

              <div className="ca-card">
                <div className="ca-namegrid">
                  <div className="ca-field">
                    <label>First name</label>
                    <input required value={su.first_name} data-testid={TID.signupFirstName}
                      onChange={(e) => setSu((s) => ({ ...s, first_name: e.target.value }))} />
                  </div>
                  <div className="ca-field">
                    <label>Last name</label>
                    <input required value={su.last_name} data-testid={TID.signupLastName}
                      onChange={(e) => setSu((s) => ({ ...s, last_name: e.target.value }))} />
                  </div>
                </div>
                <div className="ca-field">
                  <label>Work email</label>
                  <input required type="email" value={su.email} data-testid={TID.signupEmail}
                    onChange={(e) => setSu((s) => ({ ...s, email: e.target.value }))} />
                  <div className="ca-help">This is what you’ll sign in with.</div>
                </div>
                <div className="ca-field">
                  <label>Password</label>
                  <input required type="password" value={su.password} data-testid={TID.signupPassword}
                    onChange={(e) => setSu((s) => ({ ...s, password: e.target.value }))} />
                  <div className="ca-pwlist" data-testid="ca-pw-checklist">
                    <PwItem ok={pwReqs.len}>12+ characters</PwItem>
                    <PwItem ok={pwReqs.case}>Upper and lower case</PwItem>
                    <PwItem ok={pwReqs.num}>A number</PwItem>
                    <PwItem ok={pwReqs.spec}>A special character</PwItem>
                    <PwItem ok={pwReqs.seq}>No character 3 times in a row</PwItem>
                  </div>
                </div>
                <div className="ca-field">
                  <label>Practice legal name</label>
                  <input value={su.covered_entity_name} data-testid="signup-covered-entity"
                    placeholder="As it appears on your claims"
                    onChange={(e) => setSu((s) => ({ ...s, covered_entity_name: e.target.value }))} />
                  <div className="ca-help">This is the Covered Entity named on the agreement below.</div>
                </div>
              </div>

              <div className="ca-card">
                <div className="ca-baa-titlerow">
                  <div className="ca-baa-title">Business Associate Agreement<span className="ca-baa-ver">v{baaVer}</span></div>
                  <div className="ca-baa-actions">
                    <button type="button" onClick={() => setShowBaa(true)} data-testid="signup-baa-open">Read the full agreement ↗</button>
                  </div>
                </div>
                <div className="ca-baa-summary">
                  <div className="ca-caps">What it means, in short</div>
                  <ul className="ca-baa-bullets">
                    <li><CheckIcon />AriaOne protects the patient information you upload, as HIPAA requires.</li>
                    <li><CheckIcon />We use it only to run AriaOne for your practice.</li>
                    <li><CheckIcon />If a breach ever happens, we tell you.</li>
                    <li><CheckIcon />If you leave, we return or destroy your data.</li>
                  </ul>
                  <div className="ca-baa-foot">This summary is not the agreement — the full text is what you accept.</div>
                </div>
                <label className="ca-baa-accept" data-testid="ca-baa-accept-label">
                  <input type="checkbox" checked={su.baa_accepted} data-testid="signup-baa-checkbox"
                    onChange={(e) => setSu((s) => ({ ...s, baa_accepted: e.target.checked }))} />
                  <span>I, <b>{fullName || "[your name]"}</b>, have read and accept the Business Associate Agreement v{baaVer} on behalf of <b>{su.covered_entity_name.trim() || "[your practice]"}</b>, as the Covered Entity.</span>
                </label>
              </div>

              <div className={`ca-invite ${inviteOpen ? "open" : ""}`}>
                <button type="button" className="ca-invite-toggle" data-testid="ca-invite-toggle"
                  onClick={() => setInviteOpen((o) => {
                    const next = !o;
                    setSuInviteValid(next && su.invite_code.trim().length >= 4);
                    return next;
                  })}>
                  Joining your team? Enter an invite code
                  <svg className="ca-chev" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
                </button>
                {inviteOpen && (
                  <div className="ca-invite-body">
                    <div className="ca-field">
                      <label>Invite code</label>
                      <input value={su.invite_code} data-testid="signup-invite-code" placeholder="From your practice admin"
                        onChange={(e) => { const v = e.target.value; setSu((s) => ({ ...s, invite_code: v })); setSuInviteValid((v || "").trim().length >= 4); }} />
                      <div className="ca-help">With a code you join your team’s workspace — their plan covers you, so you skip the card step.</div>
                    </div>
                  </div>
                )}
              </div>

              <button type="submit" className="ca-submit" data-testid={TID.signupSubmit}
                disabled={busy || !su.baa_accepted || !su.covered_entity_name.trim()}>
                {busy ? (<><span className="revq-spinner" style={{ borderTopColor: "#fff", borderColor: "rgba(255,255,255,.4)" }} /> Preparing…</>)
                  : missingLabel ? missingLabel
                  : inviteActive ? "Join workspace →" : "Continue to card →"}
              </button>
              <div className="ca-under">Your acceptance and the agreement version are saved with your account.</div>
            </form>
          ) : (
            <div className="ca-col2" data-testid="ca-step2">
              <div className="ca-left">
                <h2 className="ca-h2">Your first month is free. <span className="ca-grad">Every feature. No setup.</span></h2>
                <p className="ca-sub">Last step: add a card to hold your spot. Nothing is charged today, and you land straight in AriaOne.</p>
                <div className="ca-price">$0 <span>due today</span></div>
                <div>
                  <div className="ca-caps">Included in your free month</div>
                  <div className="ca-feat">
                    {FEATURES.map((f) => (
                      <div className="ca-feat-item" key={f.name}>
                        <CheckIcon />
                        <div><div className="ca-feat-name">{f.name}</div><div className="ca-feat-desc">{f.desc}</div></div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="ca-bill">
                  <div className="ca-caps">How billing works</div>
                  <div className="ca-timeline">
                    <TlRow fill k="Today" v="Full access to every feature. $0." />
                    <TlRow k="Day 27" v="We email a reminder with what you’ve used so far." />
                    <TlRow last k="Day 30" v="Credit-based billing starts — you pay only for what you use." />
                  </div>
                </div>
              </div>

              <div className="ca-right">
                <div className="ca-pay">
                  <div>
                    <div className="ca-pay-h">Start your free month</div>
                    <div className="ca-pay-sub">$0 charged today. Your card stays on file for when billing starts.</div>
                  </div>
                  <div className="ca-sumrow">
                    <div>
                      <div className="ca-sum-1">{fullName} · {su.email}</div>
                      <div className="ca-sum-2">{su.covered_entity_name}</div>
                    </div>
                    <button type="button" className="ca-sum-edit" data-testid="ca-summary-edit"
                      onClick={() => { if (!pollingMsg) setScreen(SCREEN.SIGNUP1); }}>Edit</button>
                  </div>

                  {pollingMsg ? (
                    <div className="ca-status" role="status" data-testid="ca-finalize-status">
                      <span className="ca-status-tick"><CheckIcon white /></span>
                      <div className="ca-status-h">Card saved. Setting up your workspace…</div>
                      <div className="ca-status-s">This usually takes a few seconds — you’ll land in AriaOne when it’s ready.</div>
                      <div className="ca-progress" />
                      {showManualContinue && (
                        <button type="button" className="ca-continue-link" data-testid="signup-finalize-continue"
                          onClick={() => stripeIntent?.session_id && beginFinalizePolling(stripeIntent.session_id)}>
                          Taking a while? <b>Continue</b>
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="ca-stripe-mount" data-testid="ca-stripe-mount">
                        {stripeIntent && stripePromise ? (
                          <EmbeddedCheckoutProvider stripe={stripePromise}
                            options={{ clientSecret: stripeIntent.client_secret, onComplete: () => beginFinalizePolling(stripeIntent.session_id) }}>
                            <EmbeddedCheckout />
                          </EmbeddedCheckoutProvider>
                        ) : (
                          <div style={{ padding: "30px 0", textAlign: "center", fontSize: 12, color: "#6E6E6B" }}>
                            <span className="revq-spinner" style={{ borderTopColor: "#000", borderColor: "rgba(0,0,0,.18)" }} /> Loading Stripe secure form…
                          </div>
                        )}
                      </div>
                      <div className="ca-hair" />
                      <div className="ca-fine">We email you before your free month ends. Cancel anytime in Settings.</div>
                      <div className="ca-fine">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6E6E6B" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                        Card details go to Stripe. AriaOne keeps only a reference to the saved card.
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>

        <footer className="ca-footer">
          <span>© 2026 Aria One, LLC</span><span className="ca-dot">·</span>
          <span className="ca-flink">Terms</span><span className="ca-dot">·</span>
          <span className="ca-flink">Privacy Policy</span><span className="ca-dot">·</span>
          <button className="ca-fbaa" onClick={() => setShowBaa(true)} data-testid="ca-footer-baa">Business Associate Agreement</button><span className="ca-dot">·</span>
          <span className="ca-flink">Support</span>
        </footer>

        {showBaa ? (
          <div data-testid="signup-baa-modal"
            style={{ position: "fixed", inset: 0, zIndex: 9000, background: "rgba(20,20,18,.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
            onClick={(e) => { if (e.target === e.currentTarget) setShowBaa(false); }}>
            <div style={{ background: "#F6F6F4", borderRadius: 20, padding: 28, width: "min(680px, 96vw)", boxShadow: "0 30px 80px rgba(20,20,18,.35)" }}>
              <BaaReader onClose={() => setShowBaa(false)} />
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="auth-bg lg-page" data-testid="auth-gate">
      <div className="lg-row">
      <div className="lg-stage">
      <BrandPanel formColRef={formColRef} />
      <div className="lg-form-col" ref={formColRef}>
        <a className="lg-logo-link" href="https://www.ariaone.ai/" target="_blank" rel="noreferrer" data-testid="login-logo-link">
          <img className="lg-logo" src="/assets/ariaone_logo_black.png" alt="aria one" />
        </a>
      <div className="lg-form-inner">
      <div className={`auth-card ${wide ? "wide" : ""}`}>
        {/* ─── SIGN IN ─── */}
        <div className={`auth-screen ${screen === SCREEN.SIGNIN ? "on" : ""}`}>
          <div className="auth-h">Welcome back</div>
          <div className="auth-s">Sign in to your AriaOne workspace.</div>

          {bannerExpiring ? (
            <div className="auth-banner ab-warn">
              <b>Your password expires in 5 days.</b> You can change it now or after you sign in.
            </div>
          ) : null}
          {bannerExpired ? (
            <div className="auth-banner ab-err">
              <b>Your session expired.</b> Sign in again to continue — your work is saved.
            </div>
          ) : null}
          {siError ? (
            <div data-testid={TID.loginError} style={{ marginTop: 12, display: "flex", alignItems: "flex-start", gap: 8, background: "#FDEDEC", border: "1px solid #F3D3D0", borderRadius: 9, padding: "10px 12px", fontSize: 12, color: "#C4302A", lineHeight: 1.45 }}>
              <svg viewBox="0 0 14 14" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="7" cy="7" r="5.6" /><path d="M7 4.3v3.4M7 9.6v.1" /></svg>
              <span>{siError}</span>
            </div>
          ) : null}

          <form onSubmit={onSignIn} autoComplete="on">
            <div className="af">
              <label>Email</label>
              <input
                type="email"
                autoComplete="email"
                required
                data-testid={TID.loginEmail}
                value={si.email}
                onChange={(e) => setSi((s) => ({ ...s, email: e.target.value }))}
              />
            </div>
            <div className="af">
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                <label>Password</label>
                <span onClick={() => { setForgotMsg(""); setForgotEmail(si.email); setScreen(SCREEN.FORGOT); }} data-testid="signin-forgot" style={{ fontSize: 12, fontWeight: 600, color: "#4A4AC2", cursor: "pointer" }}>Forgot password?</span>
              </div>
              <input
                type="password"
                autoComplete="current-password"
                required
                data-testid={TID.loginPassword}
                value={si.password}
                onChange={(e) => setSi((s) => ({ ...s, password: e.target.value }))}
              />
            </div>
            <button
              type="submit"
              className="auth-btn"
              disabled={busy}
              data-testid={TID.loginSubmit}
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="auth-div">New to AriaOne?</div>
          <button
            className="btn-create"
            onClick={() => { setSuError(""); setScreen(SCREEN.SIGNUP1); }}
            data-testid={TID.signupToggle}
          >
            Start free for 30 days
            <img src="/assets/login/icon-arrow-right.svg" alt="" />
          </button>
          <div className="lg-micro">No setup. No IT. About 2 minutes.</div>
        </div>

        {/* ─── MFA (UI ONLY) ─── */}
        <div className={`auth-screen ${screen === SCREEN.MFA ? "on" : ""}`}>
          <div className="auth-back" onClick={() => setScreen(SCREEN.SIGNIN)}>← Back</div>
          <div className="auth-h">Two-step verification</div>
          <div className="auth-s">
            We sent a 6-digit code to your phone ending in <b>•1234</b>
          </div>
          <OtpRow value={mfaOtp} onChange={setMfaOtp} testIdPrefix="mfa-otp" />
          <button
            className="auth-btn"
            onClick={() => toast.message("MFA endpoint not yet exposed by the API")}
          >
            Verify &amp; sign in
          </button>
          <div className="state-links">
            <span onClick={() => toast("New code sent via SMS")}>Resend code</span>
          </div>
        </div>

        {/* ─── FORGOT ─── */}
        <div className={`auth-screen ${screen === SCREEN.FORGOT ? "on" : ""}`}>
          <div className="auth-back" onClick={() => setScreen(SCREEN.SIGNIN)}>← Back to sign in</div>
          <div className="auth-h">Reset your password</div>
          <div className="auth-s">
            Enter your account email — we&apos;ll text a one-time code to your verified phone number
          </div>
          {forgotMsg ? (
            <div className="auth-banner ab-err">{forgotMsg}</div>
          ) : null}
          <form onSubmit={onForgotSubmit}>
            <div className="af">
              <label>Email</label>
              <input
                type="email"
                required
                value={forgotEmail}
                data-testid="forgot-email-input"
                onChange={(e) => setForgotEmail(e.target.value)}
              />
            </div>
            <button
              type="submit"
              className="auth-btn"
              disabled={busy}
              data-testid="forgot-submit-btn"
            >
              {busy ? "Sending…" : "Send reset code"}
            </button>
          </form>
        </div>

        {/* ─── RESET ─── */}
        <div className={`auth-screen ${screen === SCREEN.RESET ? "on" : ""}`}>
          <div className="auth-back" onClick={() => setScreen(SCREEN.SIGNIN)}>← Back to sign in</div>
          <div className="auth-h">Enter code &amp; new password</div>
          <div className="auth-s">
            Code sent to your phone · valid 10 minutes
          </div>
          {resetError ? (
            <div className="auth-banner ab-err">{resetError}</div>
          ) : null}
          <form onSubmit={onResetSubmit}>
            <OtpRow value={resetOtp} onChange={setResetOtp} testIdPrefix="reset-otp" />
            <div className="af">
              <label>New password</label>
              <input
                type="password"
                required
                value={resetPw}
                data-testid="reset-password-input"
                onChange={(e) => setResetPw(e.target.value)}
              />
            </div>
            <ReqList r={resetPwReqs} />
            <button
              type="submit"
              className="auth-btn"
              disabled={busy}
              data-testid="reset-submit-btn"
            >
              {busy ? "Resetting…" : "Reset password"}
            </button>
          </form>
        </div>

        {/* ─── LOCKED (UI ONLY) ─── */}
        <div className={`auth-screen ${screen === SCREEN.LOCKED ? "on" : ""}`}>
          <div className="auth-back" onClick={() => setScreen(SCREEN.SIGNIN)}>← Back</div>
          <div className="auth-h">Account locked</div>
          <div className="auth-s">
            Too many failed sign-in attempts. For your protection, this account is temporarily locked.
          </div>
          <div className="auth-banner ab-err">
            <b>5 failed attempts detected.</b> Unlock with a one-time SMS code and set a new password — or wait 30 minutes and try again.
          </div>
          <button
            className="auth-btn"
            onClick={() => { setForgotEmail(si.email); setScreen(SCREEN.FORGOT); }}
          >
            Unlock with SMS code
          </button>
          <button className="auth-btn ghost" onClick={() => setScreen(SCREEN.SIGNIN)}>
            Try again later
          </button>
        </div>

      </div>{/* end auth-card */}
      </div>{/* end lg-form-inner */}

      <div className="lg-foot" data-testid="signin-footer">
        <div className="lf-trust">
          <button className="lf-pill" onClick={() => setSigninBaa(true)} data-testid="signin-baa-open" title="Opens the Business Associate Agreement">
            <img src="/assets/login/icon-hipaa-document.svg" alt="" />
            HIPAA · under a BAA
          </button>
          <span>Data isolated per workspace · 15-minute sessions</span>
        </div>
        <div className="lf-legal">
          <span>© 2026 Aria One, LLC</span><span className="lf-dot">·</span>
          <span className="lf-link">Terms</span><span className="lf-dot">·</span>
          <span className="lf-link">Privacy Policy</span>
        </div>
        <div className="lf-legal">
          <a className="lf-baa" onClick={(e) => { e.preventDefault(); setSigninBaa(true); }} data-testid="signin-baa-legal">BAA</a><span className="lf-dot">·</span>
          <span className="lf-link">Status</span><span className="lf-dot">·</span>
          <span className="lf-link">Support</span>
        </div>
      </div>
      </div>{/* end lg-form-col */}
      </div>{/* end lg-stage */}
      </div>{/* end lg-row */}

      {signinBaa ? (
        <div data-testid="signin-baa-modal"
          style={{ position: "fixed", inset: 0, zIndex: 9000, background: "rgba(20,20,18,.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={(e) => { if (e.target === e.currentTarget) setSigninBaa(false); }}>
          <div style={{ background: "#F6F6F4", borderRadius: 20, padding: 28, width: "min(680px, 96vw)", boxShadow: "0 30px 80px rgba(20,20,18,.35)" }}>
            <BaaReader onClose={() => setSigninBaa(false)} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const FEATURES = [
  { name: "Claims & eligibility", desc: "Submit, track and verify in one place." },
  { name: "AI chart documents", desc: "Draft notes and letters in seconds." },
  { name: "Denial worklists", desc: "See what to fix, ranked by impact." },
  { name: "Payment posting", desc: "ERAs matched and reconciled for you." },
  { name: "Patient statements", desc: "Clear, on-brand, ready to send." },
  { name: "Reporting", desc: "Revenue and A/R at a glance." },
  { name: "Team workspace", desc: "Invite staff with the right access." },
];

function CheckIcon({ white }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={white ? "#fff" : "#141412"} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function PwItem({ ok, children }) {
  return (
    <span className={`ca-pwitem ${ok ? "ok" : ""}`}>
      <span className="ca-pwmark">
        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
      </span>
      {children}
    </span>
  );
}

function TlRow({ k, v, fill, last }) {
  return (
    <div className="ca-tl">
      <div className="ca-tl-rail">
        <span className={`ca-tl-dot ${fill ? "fill" : "ring"}`} />
        {!last && <span className="ca-tl-conn" />}
      </div>
      <div className="ca-tl-body">
        <div className="ca-tl-k">{k}</div>
        <div className="ca-tl-v">{v}</div>
      </div>
    </div>
  );
}

function ReqList({ r }) {
  return (
    <div className="req-list">
      <div className={`req ${r.len ? "ok" : ""}`}>12+ characters</div>
      <div className={`req ${r.case ? "ok" : ""}`}>Upper &amp; lower case</div>
      <div className={`req ${r.num ? "ok" : ""}`}>A number</div>
      <div className={`req ${r.spec ? "ok" : ""}`}>A special character</div>
      <div className={`req ${r.seq ? "ok" : ""}`}>No character 3 times in a row</div>
    </div>
  );
}

function OtpRow({ value, onChange, testIdPrefix }) {
  const refs = useRef([]);
  return (
    <div className="otp-row">
      {value.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          className="otp"
          maxLength={1}
          inputMode="numeric"
          value={d}
          data-testid={`${testIdPrefix}-${i}`}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "").slice(-1);
            const next = [...value];
            next[i] = v;
            onChange(next);
            if (v && i < 5) refs.current[i + 1]?.focus();
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !value[i] && i > 0) {
              refs.current[i - 1]?.focus();
            }
          }}
        />
      ))}
    </div>
  );
}
